import type { CategoryPrompt } from "../types";

import React from "react";

import {
  CalendarDays,
  Clock,
  Coffee,
  Cookie,
  Flame,
  Leaf,
  Package,
  WheatOff,
} from "lucide-react";

export const foodPrompts: CategoryPrompt[] = [
  {
    icon: <Cookie />,
    title: "Dietary Community Listicle Advertorials",
    description:
      "Build a listicle targeting specific dietary communities, featuring my verified products with CTAs throughout.",
    prompt: `Build a dietary-focused listicle that addresses specific community needs and positions your verified products as safe, compliant solutions.

**DIETARY FOCUS:** e.g., "10 Keto Snacks That Actually Taste Good" OR "Vegan Protein Sources Beyond Tofu" OR "Gluten-Free Finds for Celiacs"

**YOUR PRODUCTS:** e.g., "Our certified keto snacks solve #3" OR "Our plant protein is option #7"

**Community Reader:** Choose one:
- Strict followers (medically required or committed)
- Lifestyle adopters (health/ethics motivated)
- Newly diagnosed (learning what's safe)

**Tone & Style:**
- Community insider language
- Empathetic to dietary challenges
- Celebration of good options
- Verification-focused for trust

**CTA Guidelines:** Each CTA should:
- Reference dietary compliance ("Certified Keto")
- Use community language ("Celiac-Safe")
- Include verification badges ("Tested & Verified")
- Emphasize taste ("Finally, Something Good")

**Conversion Tactics Use:**
- Certification badges prominently displayed
- Macro/nutrition breakdowns relevant to diet
- Taste emphasis (dispel "diet food" stigma)
- Community testimonials from same dietary group
- Cross-contamination safety for allergen diets
- Variety to prevent diet fatigue

**Key Elements to Include:**
- **Hook:** Relatable dietary struggle (2-3 paragraphs on community challenge)
- **List Items (8-12 products):** Numbered option → why it works for diet → your product placement
- **Product Structure Per Item:**
  - Item headline: "#X: [Product type that solves craving/need]"
  - Why this category matters for diet
  - Common issues with typical options
  - Your product as solution
  - Dietary specs: Macros, certifications, ingredients
  - Certification badges: Third-party verified logos
  - Taste description: Breaking "diet food tastes bad" myth
  - Community testimonial: Someone on same diet loving it
- **Product Callouts:** Dietary compliance highlights with "Shop Now" CTA
- **Certification Displays:** Keto-certified, Certified Gluten-Free, Vegan Society logos (2-3 throughout)
- **Nutritional Breakdowns:** Relevant macros/ingredients per dietary need
- **Cross-Contamination Info:** For allergen-focused diets (gluten, dairy, nuts)
- **Community Stories:** Real people on this diet enjoying products
- **Dietary Bundle:** Variety pack for this specific community
- **CTAs:** After item #3, after item #7, conclusion (5-7 total)
- **FAQ:** 4-5 questions (Actually compliant? Cross-contamination? Verified how? Taste really good?)

**Goal:** Convert dietary community members into loyal customers by demonstrating certified compliance, addressing their specific nutritional needs, and proving your products taste good enough to make their restrictive diet enjoyable and sustainable.`,
  },
  {
    icon: <Package />,
    title: "Use Case Solution Page",
    description:
      "Create a page targeting [a specific use case] with product comparisons, ingredient transparency, and testimonials from relevant users.",
    prompt: `Build a use-case specific solution page that addresses a targeted food need with product options, ingredient clarity, and social proof from relevant users.

**USE CASE:** e.g., "Post-Workout Recovery Nutrition" OR "Kids' Lunchbox Healthy Options" OR "On-the-Go Meal Replacement" OR "Late-Night Snacking Without Guilt"

**SOLUTION ANGLE:** e.g., Convenience, nutrition, portion control, dietary compliance

**Target User:** Choose one:
- Problem-solving (specific need to meet)
- Convenience-seeking (time-constrained)
- Health-optimizing (performance or wellness goal)

**Tone & Style:**
- Solution-focused and practical
- Empathetic to specific challenge
- Results-oriented not generic
- Lifestyle context-aware

**CTA Guidelines:** Each CTA should:
- Reference use case ("Perfect for Meal Prep")
- Use outcome language ("Fuel Your Workouts")
- Include relevant benefit ("20g Protein")
- Emphasize suitability ("Made for Busy Parents")

**Conversion Tactics Use:**
- Use-case specific nutrition needs
- Product comparison for different scenarios
- Ingredient breakdown relevant to use
- Success stories from same use case
- Portion/serving guidance
- Bundle options for use case routine

**Key Elements to Include:**
- **Hero Section:** Use case headline, solution image, primary benefit, "Shop Solution" CTA
- **The Challenge:** Why this use case needs specific food solution (2-3 paragraphs)
- **Product Comparison:** 3-5 options compared for this use case (chart format)
- **Comparison Categories:** Nutrition, convenience, taste, price, portability
- **Ingredient Transparency:** Full ingredient lists with why each matters for use case
- **Nutritional Breakdown:** Macros, calories, key nutrients relevant to scenario
- **Usage Guidance:** When/how to consume for optimal results in this context
- **Use-Case Testimonials (4-6):** Real users in same scenario with specific results
- **Daily Routine Integration:** How product fits into their typical day
- **Prep/Storage Tips:** Practical advice for use case (lunchbox safe, shelf-stable, etc.)
- **Portion Sizing:** Appropriate amounts for this specific need
- **Bundle Options:** Multi-pack or variety for weekly use case needs
- **Alternative Options:** Different products for variations of use case
- **CTAs:** Hero, after comparison, with testimonials, bundle section (5-6 total)
- **FAQ:** 5-7 questions (Shelf life? Prep needed? Allergens? Kids safe? Portion size?)

**Goal:** Convert use-case specific shoppers into confident buyers by demonstrating your products perfectly solve their particular food challenge with appropriate nutrition, convenience, and real-world proof from people in identical situations.`,
  },
  {
    icon: <WheatOff />,
    title: "Subscription Landing Pages",
    description:
      "Create a subscription page with unboxing video, cost breakdown, customization options, and skip/cancel flexibility.",
    prompt: `Build a subscription landing page that converts one-time buyers into recurring customers by emphasizing value, customization, and total flexibility.

**SUBSCRIPTION OFFER:** e.g., "Monthly Snack Box" OR "Weekly Meal Kit Delivery" OR "Protein Bar Subscription"

**VALUE PROPOSITION:** e.g., "Save 30% + never run out" OR "Curated variety delivered monthly"

**Subscription Model:** Choose one:
- Replenishment (same favorites, regular delivery)
- Curated boxes (surprise selection each month)
- Build-your-own (customize products and frequency)

**Tone & Style:**
- Convenience and value-focused
- Excitement about discovery
- Flexibility and control emphasized
- "One less thing to worry about"

**CTA Guidelines:** Each CTA should:
- Emphasize savings ("Save 30% Every Order")
- Use convenience language ("Set It & Forget It")
- Reference flexibility ("Cancel Anytime")
- Include first-box incentive ("Get 50% Off First Box")

**Conversion Tactics Use:**
- Annual savings calculator
- Unboxing video showing experience
- First box discount (significant)
- Comparison: subscription vs. one-time pricing
- Customization preview tool
- Skip/pause/cancel ease demonstration

**Key Elements to Include:**
- **Hero Section:** Subscription box image, headline with savings %, "Start Subscription" CTA
- **Unboxing Video:** 60-90 sec showing what arrives, packaging, products
- **Value Proposition:** 3-4 key benefits (save money, convenience, variety, exclusive items)
- **Cost Breakdown:**
  - Monthly price vs. retail value
  - Per-item cost comparison
  - Annual savings calculator: "Save $XXX/year"
- **How It Works:** 3-step process (Choose plan → Customize → Delivered monthly)
- **Customization Options:**
  - Frequency selector (weekly, bi-weekly, monthly)
  - Quantity/size options
  - Product preferences or exclusions
  - Dietary filters if applicable
- **Box Preview:** Visual of what's included this month/season
- **Flexibility Features:**
  - Skip delivery badge + explanation
  - Pause subscription option
  - Cancel anytime promise (no fees)
  - Swap products easily
  - Change delivery dates
- **First Box Incentive:** "50% off first delivery + free gift"
- **Subscriber Perks:**
  - 30% off all items always
  - Free shipping every time
  - Early access to new products
  - Exclusive subscriber-only items
  - Birthday surprises or milestone rewards
- **Social Proof:** "85,000+ active subscribers" + testimonials focused on value/convenience
- **Pricing Display:**
  - Per delivery: $XX (instead of $XX retail)
  - Savings badge: "Save 30%"
  - No hidden fees disclosure
- **CTAs:** Hero, after video, after pricing, after flexibility, footer (5-6 total)
- **FAQ:** 6-8 questions (Cancel how? Skip months? Change products? Shipping? Gift subscriptions? Pause?)

**Goal:** Convert hesitant shoppers into subscribers by demonstrating massive savings, showing the delightful unboxing experience, and proving flexibility through easy skip/pause/cancel options that make commitment feel risk-free and valuable.`,
  },
  {
    icon: <Leaf />,
    title: "Seasonal Drop Bundle Pages",
    description:
      "Create a seasonal bundle page creating urgency around limited availability, featuring all seasonal SKUs with collection incentive.",
    prompt: `Build a seasonal drop bundle page that creates urgency around limited-time flavors with collection incentives and FOMO-driven messaging.

**SEASONAL DROP:** e.g., "Fall Harvest Collection" OR "Summer Refreshers Box" OR "Holiday Flavor Bundle"

**AVAILABILITY:** e.g., "Available October-November only" OR "While supplies last"

**Shopper Type:** Choose one:
- Seasonal enthusiasts (love limited editions)
- Collectors (want complete sets)
- FOMO buyers (can't miss exclusives)

**Tone & Style:**
- Seasonal excitement and nostalgia
- Urgent but celebratory
- Exclusive limited-time framing
- "Only once a year" emphasis

**CTA Guidelines:** Each CTA should:
- Emphasize seasonality ("Get Fall Flavors")
- Use scarcity language ("Limited Time Only")
- Reference collection ("Complete Your Set")
- Include bundle savings ("Save on Full Collection")

**Conversion Tactics Use:**
- Countdown to seasonal end date
- Inventory counters on popular items
- Bundle discount for complete collection
- "Last year sold out in X days" social proof
- Individual SKU availability indicators
- Pre-order for next season waitlist

**Key Elements to Include:**
- **Hero Section:** Seasonal imagery, collection headline, availability window, "Shop Collection" CTA
- **Availability Countdown:** Timer to when seasonal items disappear
- **Complete Collection Bundle:** All seasonal SKUs with bundle discount
- **Individual SKU Showcase:** 4-8 seasonal products with descriptions
- **Flavor Profiles:** Detailed tasting notes for each seasonal item
- **Why This Season:** What makes these flavors perfect for now
- **Last Year's Proof:** "2025 Fall Collection sold out in 12 days"
- **Inventory Indicators:** "Low stock" or "X remaining" on popular flavors
- **Mix & Match Options:** Build your own seasonal bundle (pick 3, 6, 12)
- **Limited Availability Badge:** "Never available again" or "Once yearly release"
- **Gift Set Option:** Seasonal bundle in gift packaging
- **Social Proof:** Customer photos from previous seasonal drops
- **Comparison:** Bundle savings vs. individual purchase
- **Seasonal Pairing Ideas:** How to enjoy these flavors
- **Next Season Waitlist:** "Join list for Spring 2026 drops"
- **CTAs:** Hero, complete bundle, individual items, waitlist (5-6 total)
- **FAQ:** 4-5 questions (When ends? Restock? Save for later? Gift packaging? Next season?)

**Goal:** Maximize seasonal revenue by creating urgency through limited availability messaging and incentivizing complete collection purchases with bundle discounts that drive higher AOV while building FOMO that converts hesitant shoppers before items disappear.`,
  },
  {
    icon: <CalendarDays />,
    title: "Recipe Collections with Shoppable Products",
    description:
      "Build recipe pages with embedded video tutorials and shoppable product CTAs throughout to show product versatility.",
    prompt: `Build a recipe collection page that inspires usage versatility through video tutorials with integrated shoppable product CTAs.

**RECIPE FOCUS:** e.g., "10 Ways to Use [Product]" OR "Breakfast Recipe Collection" OR "High-Protein Meal Ideas" OR "Quick Snack Hacks"

**PRODUCTS FEATURED:** e.g., 3-8 products used across recipes

**Visitor Type:** Choose one:
- Usage inspiration seekers (bought product, need ideas)
- Recipe-first shoppers (find recipe, buy ingredients)
- Variety maximizers (get more from purchases)

**Tone & Style:**
- Approachable home cook energy
- Creative and versatile framing
- Quick and achievable recipes
- "You can do this" encouragement

**CTA Guidelines:** Each CTA should:
- Reference the recipe ("Get Ingredients")
- Use versatility language ("Try This Recipe")
- Include convenience ("Shop All Ingredients")
- Emphasize product showcase ("See It In Action")

**Conversion Tactics Use:**
- Video tutorials with product timestamps
- "Shop this recipe" one-click bundles
- Ingredient lists with shoppable links
- Recipe difficulty and time estimates
- Nutritional info per recipe
- User-generated recipe photos

**Key Elements to Include:**
- **Hero Section:** Collection headline, featured recipe video, "Browse Recipes" CTA
- **Recipe Grid:** 8-15 recipes with photos, time estimate, difficulty level
- **Featured Recipes (3-5 detailed):**
  - Large hero image or video
  - Prep/cook time
  - Difficulty level (easy, medium, advanced)
  - Ingredient list with shoppable product links
  - Step-by-step instructions
  - Nutritional info
  - Video tutorial (60-120 seconds)
  - "Shop All Ingredients" bundle CTA
- **Filter Options:** By meal type, by product used, by dietary need, by time required
- **Video Tutorials:** Embedded throughout showing product in use
- **Product Callouts:** Highlighted in recipes with quick-add CTAs
- **Recipe Bundles:** "Get everything for 3 breakfast recipes"
- **Versatility Messaging:** "One product, endless possibilities"
- **User-Generated Content:** Customer recipe photos and variations
- **Nutritional Breakdowns:** Macros, calories per serving
- **Tips & Variations:** How to modify recipes
- **Print/Save Options:** Recipe cards downloadable
- **Shopping List Generator:** Check recipes, auto-generate list
- **CTAs:** With each recipe, bundle sections, product highlights (8-12 total)
- **FAQ:** 3-4 questions (Substitutions? Prep ahead? Storage? Other uses?)

**Goal:** Increase product purchases and repeat buying by showcasing versatility through inspiring recipes that make products feel essential and multifunctional, with seamless shoppable integration that converts inspiration into immediate ingredient purchases.`,
  },
  {
    icon: <Coffee />,
    title: "Use-Case Recipe Guides",
    description:
      "Create a use-case recipe guide targeting [a specific use case] with quick recipes and meal prep strategies featuring my product as hero ingredient.",
    prompt: `Build a use-case specific recipe guide that solves a targeted food challenge with quick recipes and meal prep strategies featuring your product.

**USE CASE:** e.g., "5-Minute Breakfast for Busy Mornings" OR "Post-Workout Meal Prep" OR "Kid-Friendly Lunch Ideas" OR "High-Protein Dinner Shortcuts"

**PRODUCT AS HERO:** e.g., "Our protein powder transforms these meals" OR "Our snack bars solve lunch prep"

**Target User:** Choose one:
- Time-constrained (need speed)
- Goal-focused (fitness, health, weight)
- Specific scenario (kids, work, travel)

**Tone & Style:**
- Solution-focused and efficient
- Realistic not aspirational perfection
- Empathetic to constraints
- Practical and actionable

**CTA Guidelines:** Each CTA should:
- Reference use case ("Perfect for Meal Prep")
- Use time/outcome benefit ("Ready in 5 Minutes")
- Include product role ("Powered by [Product]")
- Emphasize ease ("No-Fail Solution")

**Conversion Tactics Use:**
- Time estimates prominent (5 min, 10 min prep)
- Batch prep strategies for use case
- Nutrition aligned to use case goal
- Success stories from same scenario
- Product as time-saver emphasis
- Weekly meal plan templates

**Key Elements to Include:**
- **Hero Section:** Use case headline, quick recipe preview, total time, "Get Started" CTA
- **The Challenge:** Why this use case needs specific solution (2-3 paragraphs)
- **Quick Recipes (5-8):**
  - Recipe name + hero image
  - Prep/total time (emphasize speed)
  - Ingredient list (your product featured)
  - Simple 3-5 step instructions
  - Nutrition info relevant to use case
  - "Why this works" for scenario
  - Video or gif showing speed
  - "Shop Ingredients" CTA
- **Meal Prep Strategy:** How to batch prepare for the week using product
- **Weekly Plan Template:** Sample schedule showing use case meals
- **Product Integration:** How product simplifies this use case throughout
- **Time-Saving Tips:** Shortcuts specific to scenario
- **Prep Ahead Guide:** What to make in advance
- **Storage Instructions:** How long recipes keep
- **Variation Options:** Adapt for different tastes/needs
- **Shopping List:** All ingredients needed for week
- **Success Metrics:** "Save X hours per week" or nutrition goals met
- **User Testimonials:** People solving same use case with these recipes
- **CTAs:** Hero, with each recipe, meal plan section, product bundles (6-8 total)
- **FAQ:** 4-5 questions (Freezer-friendly? Substitutions? Kid-approved? Scale portions?)

**Goal:** Convert use-case specific shoppers into loyal repeat customers by providing practical recipes that prove your product is the essential time-saving ingredient that makes their challenging food scenario achievable and sustainable.`,
  },
  {
    icon: <Flame />,
    title: "Product Comparison Page",
    description:
      "Create a comparison page with side-by-side ingredient lists, nutrition panels, and taste test results positioning my product as superior.",
    prompt: `Build a product comparison page that positions your food as superior through transparent side-by-side analysis of ingredients, nutrition, and taste.

**COMPARISON FOCUS:** e.g., "Us vs. [Competitor]" OR "Why We're Different" OR "The Better [Product Category]"

**COMPARISON ANGLE:** e.g., Ingredient quality, nutrition density, taste, price-per-serving, sourcing

**Visitor Type:** Choose one:
- Active comparers (researching between brands)
- Ingredient-conscious (read labels carefully)
- Value seekers (best quality for price)

**Tone & Style:**
- Factual and data-driven
- Confident not aggressive
- Transparent about differences
- Let facts speak for themselves

**CTA Guidelines:** Each CTA should:
- Reference superiority ("Taste the Difference")
- Use comparison proof ("See Why We're Better")
- Include risk-free language ("Try Risk-Free")
- Emphasize quality ("Choose Better Ingredients")

**Conversion Tactics Use:**
- Detailed side-by-side comparison charts
- Full ingredient list transparency
- Nutrition panel comparisons
- Blind taste test results
- Price-per-serving calculations
- Money-back guarantee for trial

**Key Elements to Include:**
- **Hero Section:** Bold comparison headline, "Try the Better Option" CTA
- **Side-by-Side Comparison Chart:** Detailed categories (8-12 rows):
  - Full ingredient list
  - Sugar content
  - Protein/fiber/key nutrients
  - Artificial ingredients (yes/no)
  - Preservatives/additives
  - Sourcing/certifications
  - Price per serving
  - Customer rating
- **Ingredient Breakdown:** What's better about your ingredients with specifics
- **Nutrition Highlight:** Key nutritional advantages with visual panels
- **What's NOT In Yours:** Harmful ingredients you exclude
- **Taste Test Results:** Blind taste comparison data if available
- **Quality Sourcing:** Where ingredients come from vs. competitor
- **Price Analysis:** Cost-per-serving math shown clearly
- **Certifications Display:** Your certifications vs. theirs
- **Customer Testimonials (5-8):** Switchers explaining why they changed
- **Nutritionist Quote:** Expert validation of nutritional superiority
- **Money-Back Guarantee:** Risk-free trial for skeptical comparers
- **Sample Offer:** Low-commitment first purchase option
- **Honest Acknowledgment:** If competitor has any advantage, address it
- **CTAs:** Hero, after comparison chart, with testimonials, guarantee section (5-6 total)
- **FAQ:** 4-5 questions (Taste really better? Worth price difference? Guarantee terms? Available where?)

**Goal:** Convert competitor customers and active researchers into buyers by providing transparent, factual comparison that highlights your superior ingredients and nutrition, backed by taste validation and risk-free guarantee that eliminates purchase hesitation.`,
  },
  {
    icon: <Clock />,
    title: "Launch Holiday Bundle Sale Pages",
    description:
      "Build a sale page with countdown timer, bundle savings clearly stated, and bulk purchase positioning.",
    prompt: `Build a holiday bundle sale page that drives urgency and maximizes order value through limited-time offers, clear savings, and gifting positioning.

**HOLIDAY FOCUS:** e.g., "Christmas Gift Bundles" OR "New Year Nutrition Kits" OR "Valentine's Day Treats"

**BUNDLE STRUCTURE:** e.g., "Save 40% on bundles" OR "Buy 3 Get 1 Free" OR "Gift sets 30-50% off"

**Shopper Type:** Choose one:
- Gift buyers (shopping for others)
- Bulk stockers (personal use, discount-motivated)
- Party hosts (entertaining needs)

**Tone & Style:**
- Festive and celebratory
- Urgent but joyful
- Value-focused on savings
- Gift-giving excitement

**CTA Guidelines:** Each CTA should:
- Emphasize holiday timing ("Perfect Holiday Gift")
- Use savings language ("Save 40% on Bundles")
- Reference scarcity ("Limited Holiday Stock")
- Include gifting benefit ("Gift-Ready Packaging")

**Conversion Tactics Use:**
- Countdown to holiday deadline
- Tiered bulk discounts (buy more save more)
- Gift packaging options
- Guaranteed delivery dates
- Bundle vs. individual pricing display
- Gift guide recommendations

**Key Elements to Include:**
- **Hero Section:** Holiday headline, bundle hero image, countdown timer, savings %, "Shop Bundles" CTA
- **Holiday Countdown:** Timer to order deadline for guaranteed delivery
- **Bundle Savings Display:** "Bundle of 3: Save 40%" with clear before/after pricing
- **Featured Bundles (3-6):**
  - Bundle name (e.g., "Family Favorites Pack")
  - Bundle image
  - What's included (product list)
  - Regular price vs. bundle price
  - Savings amount highlighted
  - "Perfect for [gift recipient type]"
  - "Add to Cart" CTA
- **Bulk Discount Tiers:** "Buy 2: 30% off | Buy 3: 40% off | Buy 5+: 50% off"
- **Gift Packaging Option:** Holiday wrapping or gift boxes available
- **Delivery Guarantee:** "Order by [date] for guaranteed holiday delivery"
- **Gift Guide Section:** "Best for coworkers," "Perfect for family," etc.
- **Individual Products Available:** Option to buy singles at lesser discount
- **Savings Calculator:** Visual showing total savings on bundles
- **Holiday Social Proof:** "15,000+ holiday orders already placed"
- **Gifting Features:** Gift messages, recipient shipping, gift receipts
- **Urgency Messaging:** "Holiday sale ends [date]" throughout
- **Bundle Comparison:** Show savings vs. buying individually
- **CTAs:** Hero, with each bundle, bulk discount section, footer (5-6 total)
- **FAQ:** 4-5 questions (Delivery guarantee? Gift wrapping? Returns? Extend discount?)

**Goal:** Maximize holiday revenue by creating urgency through delivery deadlines while driving higher AOV through bundle discounts and gifting positioning that converts both gift shoppers and bulk buyers stocking up for the season.`,
  },
];
